import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import {
  AssignmentRow,
  AssignmentStaticColumnDef,
  AssignmentMutableColumnDef,
  AssignmentCellRef,
  AssignmentOption,
  AssignmentOptionsResolver,
  AssignmentChangeEvent,
  AssignmentUnassignedGroup,
} from '../shared/assignment-table/interfaces/assignment-table.interface';
import {
  AssignmentExpected,
  FacilityAssignmentService,
  FacilityAssignmentDoc,
  RegionFacility,
} from './facility-assignment.service';

@Component({
  selector: 'facility-assignment',
  templateUrl: './facility-assignment.component.html',
  styleUrls: ['./facility-assignment.component.scss'],
})
export class FacilityAssignmentComponent implements OnInit, OnDestroy {
  loading = true;
  loadError: string | null = null;

  staticColumns: AssignmentStaticColumnDef[] = [
    { colId: 'displayName', headerName: 'Display Name', width: 200, pinned: 'left' },
    { colId: 'name', headerName: 'PCC Name', width: 200 },
    { colId: 'facilityId', headerName: 'Facility ID', width: 100, pinned: null },
    { colId: 'facility_code', headerName: 'Facility Code', width: 120, pinned: null },
    { colId: 'prov', headerName: 'Province', width: 80, pinned: null },
  ];

  mutableColumns: AssignmentMutableColumnDef[] = [];
  rows: AssignmentRow[] = [];
  subtitle = '';
  unassignedGroups: AssignmentUnassignedGroup[] = [];
  hiddenGroups: AssignmentUnassignedGroup[] = [];

  /** Unassigned assignments grouped by srcType */
  private unassignedBySrcType: Record<string, FacilityAssignmentDoc[]> = {};
  private hiddenBySrcType: Record<string, FacilityAssignmentDoc[]> = {};
  /** All assignments indexed by facility_settings_id + srcType */
  private allAssignments: FacilityAssignmentDoc[] = [];
  private facilityCount = 0;

  private readonly destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<void>();

  optionsResolver: AssignmentOptionsResolver = (cellRef: AssignmentCellRef, row: AssignmentRow) => {
    const srcType = cellRef.mutableColId;
    const unassigned = this.unassignedBySrcType[srcType] ?? [];
    const assignedToThisRow = this.allAssignments.filter(
      a => a.facility_settings_id === row.id && a.srcType === srcType,
    );

    const seen = new Set<string>();
    const options: AssignmentOption[] = [];

    for (const doc of [...assignedToThisRow, ...unassigned]) {
      if (seen.has(doc._id)) continue;
      seen.add(doc._id);
      options.push({
        id: doc._id,
        displayValue: doc.siteName,
        payload: doc,
      });
    }

    return options.sort((a, b) => a.displayValue.localeCompare(b.displayValue));
  };

  rowLabelFn = (row: AssignmentRow): string =>
    String(row.data['displayName'] || row.data['name'] || row.id);

  onUpdate = (event: AssignmentChangeEvent): Observable<void> => {
    const assignmentId = event.optionId;
    const facilitySettingsId = event.action === 'add' ? event.row.id : null;
    return this.facilityAssignmentService
      .updateAssignment(assignmentId, facilitySettingsId, this.expectedFor(assignmentId))
      .pipe(
        map(() => { this.updateLocalState(event); }),
        tap({ error: (err: unknown) => this.reloadOnConflict(err) }),
      );
  };

  onHiddenChange = (assignmentId: string, hidden: boolean): Observable<void> =>
    this.facilityAssignmentService
      .setHidden(assignmentId, hidden, this.expectedFor(assignmentId))
      .pipe(
        map(() => {
          this.moveBetweenPools(assignmentId, hidden);
          this.reload$.next();
        }),
        tap({ error: (err: unknown) => this.reloadOnConflict(err) }),
      );

  constructor(private facilityAssignmentService: FacilityAssignmentService) {}

  ngOnInit(): void {
    this.facilityAssignmentService.loadAll().pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ facilities, assignments }) => {
        this.applyLoad(facilities, assignments);
        this.loading = false;
      },
      error: () => {
        this.loadError = 'Could not load facility assignments.';
        this.loading = false;
      },
    });

    this.reload$.pipe(
      switchMap(() => this.facilityAssignmentService.loadAll().pipe(catchError(() => EMPTY))),
      takeUntil(this.destroy$),
    ).subscribe(({ facilities, assignments }) => this.applyLoad(facilities, assignments));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reload$.complete();
  }

  private applyLoad(facilities: RegionFacility[], assignments: FacilityAssignmentDoc[]): void {
    this.allAssignments = assignments;
    this.buildTable(facilities, assignments);
  }

  private expectedFor(assignmentId: string): AssignmentExpected | undefined {
    const doc = this.allAssignments.find(a => a._id === assignmentId);
    return doc ? { facility_settings_id: doc.facility_settings_id, hidden: doc.hidden === true } : undefined;
  }

  private reloadOnConflict(err: unknown): void {
    if (err instanceof HttpErrorResponse && err.status === 409) {
      this.reload$.next();
    }
  }

  private updateLocalState(event: AssignmentChangeEvent): void {
    const doc = this.allAssignments.find(a => a._id === event.optionId);
    if (!doc) return;

    const srcType = doc.srcType;

    if (event.action === 'add') {
      doc.facility_settings_id = event.row.id;
      const pool = this.unassignedBySrcType[srcType];
      if (pool) {
        this.unassignedBySrcType[srcType] = pool.filter(a => a._id !== doc._id);
      }
    } else {
      doc.facility_settings_id = null;
      if (!this.unassignedBySrcType[srcType]) {
        this.unassignedBySrcType[srcType] = [];
      }
      this.unassignedBySrcType[srcType].push(doc);
    }

    this.rebuildUnassignedGroups();
    this.updateSubtitle();
  }

  private moveBetweenPools(assignmentId: string, hidden: boolean): void {
    const doc = this.allAssignments.find(a => a._id === assignmentId);
    if (!doc) return;

    const from = hidden ? this.unassignedBySrcType : this.hiddenBySrcType;
    const to = hidden ? this.hiddenBySrcType : this.unassignedBySrcType;
    doc.hidden = hidden;
    doc.facility_settings_id = null;
    from[doc.srcType] = (from[doc.srcType] ?? []).filter(a => a._id !== doc._id);
    to[doc.srcType] = [...(to[doc.srcType] ?? []), doc];

    this.rebuildUnassignedGroups();
    this.updateSubtitle();
  }

  private rebuildUnassignedGroups(): void {
    this.unassignedGroups = this.groupBySrcType(this.unassignedBySrcType);
    this.hiddenGroups = this.groupBySrcType(this.hiddenBySrcType);
  }

  private groupBySrcType(pool: Record<string, FacilityAssignmentDoc[]>): AssignmentUnassignedGroup[] {
    return this.mutableColumns
      .map(c => c.colId)
      .map(srcType => ({
        label: srcType,
        items: (pool[srcType] ?? [])
          .map(a => ({ id: a._id, displayValue: a.siteName }))
          .sort((a, b) => a.displayValue.localeCompare(b.displayValue)),
      }))
      .filter(g => g.items.length > 0);
  }

  private updateSubtitle(): void {
    const hidden = this.allAssignments.filter(a => a.hidden).length;
    const mapped = this.allAssignments.filter(a => a.facility_settings_id !== null).length;
    const unmapped = this.allAssignments.length - mapped - hidden;
    this.subtitle = `${this.facilityCount} facilities · ${mapped} mapped · ${unmapped} unmapped · ${hidden} hidden`;
  }

  private buildTable(facilities: RegionFacility[], assignments: FacilityAssignmentDoc[]): void {
    // Discover unique srcTypes from assignments
    const srcTypes = [...new Set(assignments.map(a => a.srcType))].sort();

    this.mutableColumns = srcTypes.map(srcType => ({
      colId: srcType,
      headerName: srcType,
    }));

    // Group unassigned by srcType
    this.unassignedBySrcType = {};
    this.hiddenBySrcType = {};
    for (const a of assignments) {
      if (a.facility_settings_id !== null) continue;
      const pool = a.hidden ? this.hiddenBySrcType : this.unassignedBySrcType;
      if (!pool[a.srcType]) {
        pool[a.srcType] = [];
      }
      pool[a.srcType].push(a);
    }

    // Build assignment map: facility_settings_id -> srcType -> assignment doc IDs
    const assignmentMap = new Map<string, Record<string, string[]>>();
    for (const a of assignments) {
      if (!a.facility_settings_id) continue;
      let byType = assignmentMap.get(a.facility_settings_id);
      if (!byType) {
        byType = {};
        assignmentMap.set(a.facility_settings_id, byType);
      }
      if (!byType[a.srcType]) byType[a.srcType] = [];
      byType[a.srcType].push(a._id);
    }

    // Build rows from facilities
    this.rows = facilities.map(f => {
      const facilityAssignments: Record<string, string[]> = {};
      for (const srcType of srcTypes) {
        facilityAssignments[srcType] = assignmentMap.get(f._id)?.[srcType] ?? [];
      }
      return {
        id: f._id,
        data: {
          displayName: f.displayName,
          name: f.name,
          facilityId: f.facilityId,
          facility_code: f.facility_code,
          prov: f.prov,
        },
        assignments: facilityAssignments,
      };
    });

    this.facilityCount = facilities.length;
    this.updateSubtitle();
    this.rebuildUnassignedGroups();
  }
}
